import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Pencil,
  Briefcase,
  MapPin,
  DollarSign,
  Clock,
  Calendar,
  Building2,
  Users,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Eye,
} from "lucide-react";
import { JOB_API_END_POINT, COMPANY_API_END_POINT } from "@/utils/constant";
import Navbar from "../shared/Navbar";

/* ── helpers ── */
function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return "N/A"; }
}

function getDaysLeft(createdAt) {
  const expiry = new Date(createdAt).getTime() + 45 * 24 * 60 * 60 * 1000;
  const diff = expiry - Date.now();
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
}

function getBadgeColor(type = "") {
  const t = type.toLowerCase();
  if (t.includes("full"))     return "bg-blue-50 text-blue-700 border-blue-100";
  if (t.includes("hire"))     return "bg-emerald-50 text-emerald-700 border-emerald-100";
  if (t.includes("contract")) return "bg-amber-50 text-amber-700 border-amber-100";
  if (t.includes("part"))     return "bg-purple-50 text-purple-700 border-purple-100";
  if (t.includes("intern"))   return "bg-pink-50 text-pink-700 border-pink-100";
  return "bg-gray-100 text-gray-600 border-gray-200";
}

/* ── section wrapper ── */
function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-1 h-5 bg-[#012760] rounded-full" />
        <h2 className="text-lg font-bold text-[#1a1a2e]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

/* ── pill ── */
function Pill({ icon: Icon, text, color = "gray" }) {
  const colors = {
    gray:   "bg-gray-50 border-gray-200 text-gray-600",
    blue:   "bg-blue-50 border-blue-100 text-blue-700",
    green:  "bg-green-50 border-green-100 text-green-700",
    red:    "bg-red-50 border-red-100 text-red-600",
    yellow: "bg-yellow-50 border-yellow-100 text-yellow-700",
  };
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium ${colors[color]}`}>
      <Icon size={13} />
      <span>{text}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════ */
export default function AdminJobPreview() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [job, setJob]             = useState(null);
  const [company, setCompany]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  /* ── fetch job ── */
  useEffect(() => {
    if (!id) return;
    window.scrollTo({ top: 0, behavior: "smooth" });

    const fetchJob = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${JOB_API_END_POINT}/get/${id}`, {
          withCredentials: true,
        });
        if (res.data.status) {
          const jobData = res.data.job;
          setJob(jobData);

          // fetch company in background if companyId exists
          const cid = jobData.companyId || jobData.company?.companyId;
          if (cid) {
            axios
              .get(`${COMPANY_API_END_POINT}/get/${cid}`, { withCredentials: true })
              .then((r) => { if (r.data.status) setCompany(r.data.data); })
              .catch(() => {});
          }
        } else {
          setError("Job not found.");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load job.");
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [id]);

  /* ── parse helpers ── */
  const requirements = (() => {
    try {
      if (typeof job?.requirements === "string")
        return job.requirements.split("\n").filter(Boolean);
      if (Array.isArray(job?.requirements)) return job.requirements;
      return [];
    } catch { return []; }
  })();

  const hiringTeam = (() => {
    try {
      if (typeof job?.hiringTeam === "string") return JSON.parse(job.hiringTeam);
      if (Array.isArray(job?.hiringTeam)) return job.hiringTeam;
      return [];
    } catch { return []; }
  })();

  const daysLeft   = job ? getDaysLeft(job.createdAt) : 0;
  const companyName = company?.CompanyName || company?.name ||
                      job?.company?.CompanyName || job?.company?.name ||
                      job?.company || "Company";

  /* ── loading ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fc]">
        <Navbar />
        <div className="flex items-center justify-center h-[70vh]">
          <Loader2 className="w-8 h-8 text-[#012760] animate-spin" />
        </div>
      </div>
    );
  }

  /* ── error ── */
  if (error || !job) {
    return (
      <div className="min-h-screen bg-[#f8f9fc]">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[70vh] gap-4">
          <AlertCircle className="w-10 h-10 text-red-400" />
          <p className="text-gray-600 font-medium">{error || "Job not found."}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2 rounded-lg bg-[#012760] text-white text-sm font-semibold hover:bg-blue-900 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fc]">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12 mt-16">

        {/* ── top bar ── */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#012760] group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Jobs</span>
          </button>

          {/* admin badge + edit */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
              <Eye size={13} />
              Admin Preview
            </div>
            <button
              onClick={() => navigate(`/admin/jobs/edit/${job.jobId}`)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#012760] text-white text-sm font-semibold hover:bg-blue-900 transition-colors"
            >
              <Pencil size={14} />
              Edit Job
            </button>
          </div>
        </div>

        {/* ── hero card ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 mb-5">

          {/* company logo + title */}
          <div className="flex items-start gap-4 mb-5">
            <div className="flex-shrink-0 w-16 h-16 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
              {company?.logo ? (
                <img
                  src={company.logo}
                  alt={companyName}
                  className="w-full h-full object-contain p-1"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <Building2 className="w-7 h-7 text-gray-400" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-400 mb-0.5">{companyName}</p>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1a1a2e] leading-tight">
                {job.title}
              </h1>
            </div>
          </div>

          {/* meta row */}
          <div className="flex flex-wrap items-center gap-3 mb-5 text-sm text-gray-500">
            {job.location && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-[#012760]" />
                {job.location}
              </span>
            )}
            {job.createdAt && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-[#012760]" />
                Posted {formatDate(job.createdAt)}
              </span>
            )}
            {job.displayJobId && (
              <span className="text-xs text-gray-400 ml-auto">
                JOB ID — {job.displayJobId}
              </span>
            )}
          </div>

          {/* pills row */}
          <div className="flex flex-wrap gap-2 mb-5">
            {job.experience && (
              <Pill icon={Briefcase} text={`${job.experience} yrs exp`} color="gray" />
            )}
            {job.salary && (
              <Pill icon={DollarSign} text={job.salary} color="gray" />
            )}
            {(job.jobType || []).map((t) => (
              <span
                key={t}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium ${getBadgeColor(t)}`}
              >
                <Clock size={13} />
                {t}
              </span>
            ))}
          </div>

          {/* expiry + applicants */}
          <div className="flex flex-wrap items-center gap-3 pt-5 border-t border-gray-100">
            {/* expiry */}
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
              daysLeft === 0
                ? "bg-gray-100 text-gray-500 border-gray-200"
                : daysLeft <= 3
                ? "bg-red-50 text-red-600 border-red-100"
                : daysLeft <= 7
                ? "bg-yellow-50 text-yellow-700 border-yellow-100"
                : "bg-green-50 text-green-700 border-green-100"
            }`}>
              <Clock size={13} />
              {daysLeft === 0 ? "Expired" : `${daysLeft} days left`}
            </span>

            {/* applicants */}
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold">
              <Users size={13} />
              {job.applications?.length || 0} applicant{job.applications?.length !== 1 ? "s" : ""}
            </span>

            {/* view applicants shortcut */}
            <button
              onClick={() => navigate(`/admin/jobs/${job.jobId}/applicants`)}
              className="ml-auto text-xs font-semibold text-[#012760] border border-[#012760] px-3 py-1.5 rounded-lg hover:bg-[#012760] hover:text-white transition-colors"
            >
              View Applicants →
            </button>
          </div>
        </div>

        {/* ── content sections ── */}
        <div className="space-y-5">

          {/* description */}
          {job.description && (
            <Section title="Job Role Overview">
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                {job.description}
              </p>
            </Section>
          )}

          {/* responsibilities */}
          {job.responsibilities && (
            <Section title="Key Responsibilities">
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                {job.responsibilities}
              </p>
            </Section>
          )}

          {/* requirements */}
          {requirements.length > 0 && (
            <Section title="Qualifications">
              <ul className="space-y-3">
                {requirements.map((req, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#012760] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-600 leading-relaxed">{req}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* hiring team */}
          {hiringTeam.length > 0 && (
            <Section title="Hiring Team">
              <div className="grid sm:grid-cols-2 gap-3">
                {hiringTeam.map((member, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:border-[#012760] hover:shadow-sm transition-all"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#012760] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {member.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{member.name}</p>
                      <p className="text-xs text-gray-500 truncate">{member.position}</p>
                      {member.email && (
                        <p className="text-xs text-[#012760] truncate mt-0.5">{member.email}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* about company */}
          {company && (
            <Section title={`About ${companyName}`}>
              <div className="flex items-start gap-4">
                {company.logo && (
                  <div className="w-12 h-12 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img
                      src={company.logo}
                      alt={companyName}
                      className="w-full h-full object-contain p-0.5"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {company.description || "No company description available."}
                  </p>
                  {company.website && (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-xs font-semibold text-[#012760] hover:underline"
                    >
                      {company.website} ↗
                    </a>
                  )}
                </div>
              </div>
            </Section>
          )}

        </div>

        {/* ── bottom action bar ── */}
        <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-gray-400">
            This is an admin preview — candidates see the live version at{" "}
            <span className="font-mono text-xs text-gray-500">/description/{job.jobId}</span>
          </p>
          <button
            onClick={() => navigate(`/admin/jobs/edit/${job.jobId}`)}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#012760] text-white text-sm font-semibold hover:bg-blue-900 transition-colors"
          >
            <Pencil size={14} />
            Edit This Job
          </button>
        </div>

      </div>
    </div>
  );
}
